/**
 * /erp/manufacturing — list of manufacturing jobs (Manager+).
 *
 * Two simple filters: status (planned/started/completed/cancelled) and
 * a free-text search across code/product. Status defaults to "all"
 * because the day-to-day workflow alternates between "what's planned"
 * and "what's running"; a quick toggle is more useful than a default
 * narrowing.
 *
 * Each row links to the detail page where the lifecycle buttons live.
 * A "New job" CTA opens the create form. Empty state distinguishes
 * "no jobs yet" from "no matches for this filter".
 */

import { Link, createFileRoute } from "@tanstack/react-router";
import { Box, Factory, Filter, Loader2, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";

import type { ErpMfgJob, ErpMfgJobStatus } from "../lib/api";
import { useMfgJobs } from "../lib/erp";
import { formatQty } from "../lib/units";

export const Route = createFileRoute("/_app/erp/manufacturing/")({
  component: ManufacturingListPage,
});

const STATUSES: { id: ErpMfgJobStatus | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "planned", label: "Planned" },
  { id: "started", label: "In progress" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
];

function ManufacturingListPage() {
  const [statusFilter, setStatusFilter] = useState<ErpMfgJobStatus | "all">("all");
  const [search, setSearch] = useState("");

  const jobsQ = useMfgJobs(statusFilter === "all" ? {} : { status: statusFilter });

  const filtered = useMemo(() => {
    const items = jobsQ.data?.items ?? [];
    if (!search.trim()) return items;
    const needle = search.trim().toLowerCase();
    return items.filter(
      (j) =>
        j.code.toLowerCase().includes(needle) ||
        j.product_code.toLowerCase().includes(needle) ||
        j.product_name.toLowerCase().includes(needle) ||
        j.warehouse_code.toLowerCase().includes(needle),
    );
  }, [jobsQ.data?.items, search]);

  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Factory className="size-5 opacity-70" aria-hidden />
            Manufacturing
          </h1>
          <p className="text-sm opacity-60">
            Production jobs that consume raw materials and produce finished products.
          </p>
        </div>
        <Link
          to="/erp/manufacturing/new"
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          <Plus className="size-4" aria-hidden />
          New job
        </Link>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] p-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium opacity-80">
            <Filter className="mr-1 inline size-3" aria-hidden />
            Status
          </span>
          <div className="flex flex-wrap gap-1">
            {STATUSES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStatusFilter(s.id)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  statusFilter === s.id
                    ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                    : "border-[var(--border)] hover:bg-black/5 dark:hover:bg-white/5"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <label className="flex flex-1 flex-col gap-1">
          <span className="text-xs font-medium opacity-80">Search</span>
          <div className="flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2 py-1.5 focus-within:border-[var(--accent)]">
            <Search className="size-4 opacity-50" aria-hidden />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Job, product, or warehouse code/name"
              className="w-full bg-transparent text-sm outline-none"
            />
          </div>
        </label>
      </div>

      {jobsQ.isLoading ? (
        <p className="flex items-center gap-2 text-sm opacity-60">
          <Loader2 className="size-4 animate-spin" aria-hidden /> Loading jobs…
        </p>
      ) : jobsQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load jobs: {(jobsQ.error as Error).message}
        </p>
      ) : filtered.length === 0 ? (
        <EmptyState
          hasFilter={statusFilter !== "all" || !!search.trim()}
          totalJobs={jobsQ.data?.total ?? 0}
        />
      ) : (
        <JobTable jobs={filtered} />
      )}
    </div>
  );
}

function EmptyState({
  hasFilter,
  totalJobs,
}: {
  hasFilter: boolean;
  totalJobs: number;
}) {
  return (
    <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
      <Factory className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
      {hasFilter && totalJobs > 0 ? (
        <>
          <p className="text-sm font-medium">No matching jobs.</p>
          <p className="mt-1 text-sm opacity-60">Clear the filter or try a different search.</p>
        </>
      ) : (
        <>
          <p className="text-sm font-medium">No manufacturing jobs yet.</p>
          <p className="mt-1 text-sm opacity-60">
            Create one to start a production run. Jobs need an active product and an active
            warehouse.
          </p>
          <Link
            to="/erp/manufacturing/new"
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="size-4" aria-hidden />
            Create your first job
          </Link>
        </>
      )}
    </div>
  );
}

function JobTable({ jobs }: { jobs: ErpMfgJob[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
      <table className="w-full text-sm">
        <thead className="border-b border-[var(--border)] bg-black/[0.02] text-left text-xs uppercase tracking-wider opacity-70 dark:bg-white/[0.02]">
          <tr>
            <th className="px-3 py-2">Code</th>
            <th className="px-3 py-2">Product</th>
            <th className="px-3 py-2 text-right">Qty</th>
            <th className="px-3 py-2">Warehouse</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Created</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr
              key={job.id}
              className="border-b border-[var(--border)] last:border-0 hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
            >
              <td className="px-3 py-2">
                <Link
                  to="/erp/manufacturing/$jobId"
                  params={{ jobId: job.id }}
                  className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] text-[var(--accent)] hover:underline dark:bg-white/10"
                >
                  {job.code}
                </Link>
              </td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <Box className="size-3 opacity-60" aria-hidden />
                  <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
                    {job.product_code}
                  </span>
                  <span>{job.product_name}</span>
                </div>
              </td>
              <td className="px-3 py-2 text-right font-mono">
                {formatQty(job.qty, job.product_uom)}{" "}
                <span className="opacity-60">{job.product_uom}</span>
              </td>
              <td className="px-3 py-2">
                <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
                  {job.warehouse_code}
                </span>{" "}
                <span className="opacity-80">{job.warehouse_name}</span>
              </td>
              <td className="px-3 py-2">
                <StatusBadge status={job.status} />
              </td>
              <td className="px-3 py-2 text-xs opacity-60">
                {new Date(job.created_at).toLocaleDateString()}
                {job.created_by_name && <span className="ml-1">by {job.created_by_name}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StatusBadge({ status }: { status: ErpMfgJobStatus }) {
  const styles: Record<ErpMfgJobStatus, string> = {
    planned: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
    started: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    completed: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
    cancelled: "bg-black/10 opacity-70 dark:bg-white/10",
  };
  const labels: Record<ErpMfgJobStatus, string> = {
    planned: "Planned",
    started: "In progress",
    completed: "Completed",
    cancelled: "Cancelled",
  };
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}
